import type { ProductKind } from 'types';
import type { OutletOrServiceCategoryKind } from '../outletOrServiceCategory/types';

/** Shared kind-section vocabulary for product lists and authoring workspaces. */
export type ProductKindSectionMeta = {
  kind: ProductKind;
  title: string;
  itemTitle: string;
  createLabel: string;
  emptyLabel: string;
};

const kindSectionMeta: Record<ProductKind, ProductKindSectionMeta> = {
  MEDIA: {
    kind: 'MEDIA',
    title: 'Media products',
    itemTitle: 'Media product',
    createLabel: 'Add media product',
    emptyLabel: 'No media products.',
  },
  SERVICE: {
    kind: 'SERVICE',
    title: 'Services',
    itemTitle: 'Service',
    createLabel: 'Add service',
    emptyLabel: 'No services.',
  },
  SMART: {
    kind: 'SMART',
    title: 'Smart products',
    itemTitle: 'Smart product',
    createLabel: 'Add smart product',
    emptyLabel: 'No smart products.',
  },
};

export const productKindSections: ProductKindSectionMeta[] = [
  kindSectionMeta.MEDIA,
  kindSectionMeta.SERVICE,
  kindSectionMeta.SMART,
];

export const productKindSection = (kind: ProductKind): ProductKindSectionMeta => kindSectionMeta[kind];

/** Group products by kind in one pass for kind-section renderers. */
export function productsByKind<T extends { kind: ProductKind }>(products: T[]): Map<ProductKind, T[]> {
  const grouped = new Map<ProductKind, T[]>();
  for (const product of products) {
    const items = grouped.get(product.kind) ?? [];
    items.push(product);
    grouped.set(product.kind, items);
  }
  return grouped;
}

/**
 * Kinds authorable under one parent: service categories have no media
 * products. This is the single source for section composition and create
 * entry points on outlet/service-category surfaces.
 */
export function productKindSectionsFor(parentKind: OutletOrServiceCategoryKind): ProductKindSectionMeta[] {
  return parentKind === 'SERVICE_CATEGORY'
    ? productKindSections.filter((section) => section.kind !== 'MEDIA')
    : productKindSections;
}
